import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, ErrorBanner, Notice, PageHeader, Spinner, fmt, useLoad } from "../../components/kit";
import PricingTable from "../../components/PricingTable";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { api, errMsg } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { useT } from "../../lib/i18n";
import { FileDown, ExternalLink } from "lucide-react";

const money = (cents, cur) => new Intl.NumberFormat("en-IE", { style: "currency", currency: (cur || "eur").toUpperCase() }).format((cents || 0) / 100);
const day = (ts) => (ts ? new Date(ts * 1000).toLocaleDateString() : "—");

function InvoiceLinks({ inv, id }) {
  return (
    <span className="flex items-center gap-2">
      {inv.invoice_pdf && <a href={inv.invoice_pdf} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-purple-300 hover:text-purple-200" data-testid={`invoice-pdf-${id}`}><FileDown className="h-3.5 w-3.5" />PDF</a>}
      {inv.hosted_invoice_url && <a href={inv.hosted_invoice_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-white" data-testid={`invoice-view-${id}`}><ExternalLink className="h-3.5 w-3.5" />View</a>}
    </span>
  );
}

function Invoices({ list }) {
  const { t } = useT();
  return (
    <Card testId="billing-invoices"><div className="mb-3 text-sm font-medium text-white">{t("Invoices")}</div>
      {list.length === 0 ? <p className="text-sm text-zinc-500" data-testid="billing-invoices-empty">{t("No invoices yet. They appear here after your first payment.")}</p>
        : list.map((i) => (
          <div key={i.id} className="flex flex-col gap-1 border-t border-white/5 py-2 text-sm first:border-0 sm:flex-row sm:items-center sm:justify-between" data-testid={`invoice-row-${i.id}`}>
            <span className="text-zinc-300"><span className="font-mono text-xs text-zinc-400">{i.number || i.id}</span> · {day(i.created)} · {money(i.total, i.currency)}
              {i.billing_reason === "subscription_update" && <span className="ml-2 text-xs text-zinc-500">plan change</span>}</span>
            <span className="flex items-center gap-3"><PayBadge status={i.status} /><InvoiceLinks inv={i} id={i.id} /></span>
          </div>))}
    </Card>
  );
}

const PAY_TONE = { paid: "border-emerald-500/30 text-emerald-300", pending: "border-amber-500/30 text-amber-300", open: "border-amber-500/30 text-amber-300", draft: "border-white/10 text-zinc-300" };
const PayBadge = ({ status }) => <span className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${PAY_TONE[status] || "border-red-500/30 text-red-300"}`} data-testid="billing-pay-status">{status}</span>;

function ResumeButton({ onDone }) {
  const { can } = useAuth();
  const [busy, setBusy] = useState(false);
  if (!can("owner")) return null;
  const resume = async () => {
    setBusy(true);
    try { await api.post("/app/billing/resume"); toast.success("Downgrade undone, your plan continues"); onDone(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return <Button size="sm" className="glow-btn mt-3 rounded-full" disabled={busy} onClick={resume} data-testid="billing-resume">{busy ? "Updating…" : "Keep my plan"}</Button>;
}

function PaymentPoll({ sessionId, onDone }) {
  const [state, setState] = useState({ status: "checking" });
  const n = useRef(0);
  useEffect(() => {
    let timer;
    const tick = async () => {
      try {
        const { data } = await api.get(`/app/billing/status/${sessionId}`);
        if (data.payment_status === "paid") { setState({ status: "paid" }); onDone(); return; }
        if (data.status === "expired") { setState({ status: "expired" }); return; }
        if (++n.current >= 10) { setState({ status: "timeout" }); return; }
        timer = setTimeout(tick, 2000);
      } catch (e) { setState({ status: "error", error: errMsg(e) }); }
    };
    tick();
    return () => clearTimeout(timer);
  }, [sessionId, onDone]);
  if (state.status === "paid") return <Notice tone="ok" title="Payment confirmed — your plan is active" testId="billing-paid" />;
  if (state.status === "expired") return <Notice tone="warn" title="The checkout session expired" testId="billing-expired">No payment was taken.</Notice>;
  if (state.status === "timeout") return <Notice tone="info" title="Payment is still processing" testId="billing-processing">Refresh in a minute; your plan activates automatically once Stripe confirms.</Notice>;
  if (state.status === "error") return <ErrorBanner error={state.error} />;
  return <Notice tone="info" title="Confirming your payment with Stripe…" testId="billing-checking" />;
}

function CurrentPlan({ data }) {
  const { t } = useT();
  const rows = [[t("Monthly"), data.limits.monthly], [t("Daily"), data.limits.daily], [t("Per minute"), data.limits.per_minute]];
  return (
    <Card className="lux-border" testId="billing-current">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div><div className="text-xs uppercase tracking-[0.2em] text-zinc-500">{t("Current plan")}</div>
          <div className="mt-1 font-display text-3xl font-semibold text-white" data-testid="billing-plan-name">{data.limits.unlimited ? t("Unlimited") : data.plan.name}</div></div>
        <div className="grid grid-cols-3 gap-6 text-sm">
          {rows.map(([k, v]) => <div key={k}><div className="text-xs text-zinc-500">{k}</div><div className="font-mono text-white">{v == null ? "∞" : v.toLocaleString()}</div></div>)}
        </div>
      </div>
      {data.stripe_mode === "test" && !data.limits.unlimited && <p className="mt-4 text-xs text-amber-300">{t("Stripe is in test mode: use card 4242 4242 4242 4242. No real money moves.")}</p>}
    </Card>
  );
}

function CancelScheduled({ plan, onResumed }) {
  const ends = plan.ends_at ? new Date(plan.ends_at * 1000).toLocaleDateString() : "the end of the period";
  return (
    <Notice tone="warn" title="Subscription canceled" testId="billing-cancel-scheduled">
      Your {plan.name} plan stays active until {ends}, then the workspace returns to Free.<div><ResumeButton onDone={onResumed} /></div>
    </Notice>
  );
}

function txLabel(tx, inv) {
  const change = tx.kind === "plan_change" ? `Plan change${tx.from_plan ? ` from ${tx.from_plan}` : ""} → ` : "";
  const volume = tx.volume ? ` · ${tx.volume.toLocaleString()}/mo` : "";
  const price = inv ? `${money(inv.total, inv.currency)} incl. VAT` : `€${tx.amount.toFixed(2)} excl. VAT`;
  return `${change}${tx.plan}${volume} · ${price}`;
}

function PaymentHistory({ transactions, byId }) {
  const { t } = useT();
  if (!transactions.length) return null;
  return (
    <Card testId="billing-history"><div className="mb-3 text-sm font-medium text-white">{t("Payment history")}</div>
      {transactions.map((tx) => (
        <div key={tx.session_id} className="flex flex-col gap-2 border-t border-white/5 py-2 text-sm first:border-0 sm:flex-row sm:items-center sm:justify-between">
          <span className="text-zinc-300" data-testid={`billing-history-row-${tx.session_id}`}>{txLabel(tx, byId[tx.invoice_id])}
            {tx.payment_status === "pending" && <span className="ml-2 text-xs text-zinc-500">checkout not completed</span>}</span>
          <span className="flex items-center gap-3">{byId[tx.invoice_id] && <InvoiceLinks inv={byId[tx.invoice_id]} id={tx.session_id} />}<PayBadge status={tx.payment_status} /><span className="text-xs text-zinc-500">{fmt(tx.created_at)}</span></span>
        </div>))}
    </Card>
  );
}

function PlanPicker({ data, onChanged }) {
  if (data.limits.unlimited) {
    return <Notice tone="ok" title="Unlimited sending" testId="billing-unlimited">This workspace is owned by a super admin, so it has no plan limits and needs no subscription. AWS SES account quotas still apply.</Notice>;
  }
  return <PricingTable current={data.plan.id} currentVolume={data.plan.monthly} canceling={!!data.plan.cancel_at_period_end} onChanged={onChanged} />;
}

function BillingBody({ data, inv, reload, reloadAll }) {
  const byId = useMemo(() => Object.fromEntries((inv.data || []).map((i) => [i.id, i])), [inv.data]);
  return (
    <>
      <CurrentPlan data={data} />
      {data.plan.cancel_at_period_end && <CancelScheduled plan={data.plan} onResumed={reload} />}
      <PlanPicker data={data} onChanged={reloadAll} />
      <PaymentHistory transactions={data.transactions} byId={byId} />
      {inv.error && <ErrorBanner error={inv.error} />}
      {inv.data && <Invoices list={inv.data} />}
    </>
  );
}

export default function Billing() {
  const { t } = useT();
  const [params] = useSearchParams();
  const { data, error, loading, reload } = useLoad(() => api.get("/app/billing").then((r) => r.data));
  const sessionId = params.get("session_id");
  const { can } = useAuth();
  const inv = useLoad(() => (can("owner") ? api.get("/app/billing/invoices").then((r) => r.data.invoices) : Promise.resolve(null)));
  const invReload = inv.reload;
  const reloadAll = useCallback(() => { reload(); invReload(); }, [reload, invReload]);
  return (
    <div>
      <PageHeader title={t("nav.billing")} description={t("desc.billing")} />
      <div className="space-y-6">
        {sessionId && <PaymentPoll sessionId={sessionId} onDone={reloadAll} />}
        {params.get("canceled") && <Notice tone="info" title="Checkout canceled" testId="billing-canceled">No payment was taken.</Notice>}
        <ErrorBanner error={error} />
        {loading && !data && <Spinner />}
        {data && <BillingBody data={data} inv={inv} reload={reload} reloadAll={reloadAll} />}
      </div>
    </div>
  );
}
